import Business from "@mui/icons-material/Business";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ViewModule from "@mui/icons-material/ViewModule";
import ElectricMeter from "@mui/icons-material/ElectricMeter";
import People from "@mui/icons-material/People";
import React, { useMemo } from "react";
import Divider from "@mui/material/Divider";
import { NavLink, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../hooks/storeHooks";
import { selectTreeDataWithPaths, setMobileSidebarOpen } from "../../layoutSlice";
import CustomersTreeView from "./tree/CustomersTreeView";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import List from "@mui/material/List";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import { isAdmin, isSuperAdmin } from "../../../auth/authSlice";

type NavigationItem =
  | { kind: "header"; title: string }
  | { kind?: undefined; segment: string; title: string; icon: React.ReactNode; route: string };

const NAVIGATION: NavigationItem[] = [
  { kind: "header", title: "Administration" },
  { segment: "/customerManagement", title: "Customers", icon: <Business />, route: "/customerManagement" },
  { segment: "/siteManagement", title: "Sites", icon: <WarehouseIcon />, route: "/siteManagement" },
  { segment: "/contractManagement", title: "Contracts", icon: <HistoryEduIcon />, route: "/contractManagement" },
  { segment: "/locationManagement", title: "Locations", icon: <ViewModule />, route: "/locationManagement" },
  { segment: "/meterManagement", title: "Meters", icon: <ElectricMeter />, route: "/meterManagement" },
  { segment: "/userManagement", title: "Users", icon: <People />, route: "/userManagement" },
];

const lazyPreloadMap: Record<string, () => Promise<any>> = {
  "/customerManagement": () =>
    import("../../../../features/management/customer-management/pages/CustomerManagementPage"),
  "/siteManagement": () => import("../../../../features/management/site-management/pages/siteManagementPage"),
  "/contractManagement": () =>
    import("../../../../features/management/contract-management/pages/ContractManagementPage"),
  "/locationManagement": () =>
    import("../../../../features/management/location-management/pages/LocationManagementPage"),
  "/meterManagement": () => import("../../../../features/management/meter-management/pages/MeterManagementPage"),
  "/userManagement": () => import("../../../../features/management/user-management/pages/UserManagementPage"),
};

const generateNavigationList = (
  navigation: NavigationItem[],
  selectedSegment: string,
  // eslint-disable-next-line no-unused-vars
  handleSelect: (segment: string) => void,
) => {
  const handleMouseEnter = (route: string) => {
    const preloadFn = lazyPreloadMap[route];
    if (preloadFn) {
      preloadFn();
    }
  };

  return navigation.map((navItem, index) => {
    if (navItem.kind === "header") {
      return (
        <React.Fragment key={`header-${index}`}>
          {index !== 0 && <Divider />}
          <ListItem sx={{ marginLeft: -1.8 }}>
            <ListItemText primary={navItem.title} primaryTypographyProps={{ fontWeight: "bold" }} />
          </ListItem>
        </React.Fragment>
      );
    }

    return (
      <ListItemButton
        key={navItem.segment}
        selected={navItem.segment === selectedSegment}
        component={NavLink}
        to={navItem.route}
        onMouseEnter={() => handleMouseEnter(navItem.route)}
        onClick={() => handleSelect(navItem.segment)}
        sx={{ display: "flex", gap: 1 }}
      >
        {navItem.icon}
        <ListItemText primary={navItem.title} />
      </ListItemButton>
    );
  });
};

// Generate a list of sidebar elements from the navigation
export const SidebarLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const treeDataWithPaths = useAppSelector(selectTreeDataWithPaths);
  const location = useLocation();
  const _isAdmin = useAppSelector(isAdmin);
  const _isSuperAdmin = useAppSelector(isSuperAdmin);

  const adminManagementNavigation = useMemo(() => {
    const handleSelect = () => {
      dispatch(setMobileSidebarOpen(false));
    };

    return generateNavigationList(NAVIGATION, location.pathname, handleSelect);
  }, [dispatch, location.pathname]);

  return (
    <>
      <CustomersTreeView data={treeDataWithPaths} />
      {(_isAdmin || _isSuperAdmin) && <List>{adminManagementNavigation}</List>}
    </>
  );
};
